import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { APIProvider } from '@vis.gl/react-google-maps';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './App.css';
import './i18n'; // Initialize i18n

// Import shadcn components (used in inline components)
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { Calendar } from './components/ui/calendar';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';

// Core components (needed immediately by inline components in this file)
import { SkillSOSLogo } from './components/SkillSOSLogo';
import { ColorfulBackground } from './components/RainingTools';
import { LegalLinks, TermsAndConditions, PrivacyPolicy } from './components/LegalDocuments';
import { CurrencyProvider, CurrencySelector } from './components/CurrencySelector';
import { EnhancedAvailabilityManager } from './components/FlexibleTimeSelector';
import { UserDropdown } from './components/UserDropdown';
import { EnhancedSearch } from './components/EnhancedSearch';
import { SharedHeader } from './components/SharedHeader';
import { ClientBookingsCalendar } from './components/ClientBookingsCalendar';
import { AuthModal } from './components/AuthModal';
import { LocationModalWrapper } from './components/LocationModalWrapper';
import { ReviewModal } from './components/ReviewModal';

// Context providers (needed at app root)
import { WalletProvider } from './contexts/WalletContext';
import { InboxProvider } from './contexts/InboxContext';
import { BookingProvider } from './contexts/BookingContext';
import { FooterProvider } from './contexts/FooterContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Lazy-loaded route components (code splitting for performance)
const BeautifulHomePage = React.lazy(() => import('./components/BeautifulHomePage').then(m => ({ default: m.BeautifulHomePage })));
const PublicServicesShowcase = React.lazy(() => import('./components/PublicServicesShowcase'));
const Calculator = React.lazy(() => import('./components/Calculator').then(m => ({ default: m.Calculator })));
const ComingSoon = React.lazy(() => import('./components/ComingSoon'));
const AdminWishlist = React.lazy(() => import('./components/AdminWishlist'));
const EnhancedSpecialistProfile = React.lazy(() => import('./components/EnhancedSpecialistProfile').then(m => ({ default: m.EnhancedSpecialistProfile })));
const MapLocationSelector = React.lazy(() => import('./components/MapLocationSelector').then(m => ({ default: m.MapLocationSelector })));
const MapSearchResults = React.lazy(() => import('./components/MapSearchResults'));
const SimpleMapFlow = React.lazy(() => import('./components/SimpleMapFlow').then(m => ({ default: m.SimpleMapFlow })));
const SimpleMapResults = React.lazy(() => import('./components/SimpleMapResults').then(m => ({ default: m.SimpleMapResults })));
const GoogleMapsDemo = React.lazy(() => import('./components/GoogleMapsDemo').then(m => ({ default: m.GoogleMapsDemo })));
const MockBookingsDemo = React.lazy(() => import('./components/MockBookingsDemo').then(m => ({ default: m.MockBookingsDemo })));
const MyBookings = React.lazy(() => import('./components/MyBookings'));
const BookingRequests = React.lazy(() => import('./components/BookingRequests'));
const GuaranteePage = React.lazy(() => import('./pages/GuaranteePage'));
const UpdatePhonePage = React.lazy(() => import('./pages/UpdatePhonePage'));
const StripeHelpPage = React.lazy(() => import('./pages/StripeHelpPage'));
const GuestPaymentPage = React.lazy(() => import('./pages/GuestPaymentPage'));
const StripeGuidePage = React.lazy(() => import('./pages/StripeGuidePage'));
const CleanBubblesGame = React.lazy(() => import('./pages/CleanBubblesGame'));
const GamesHub = React.lazy(() => import('./pages/GamesHub'));
const MyProfile = React.lazy(() => import('./components/MyProfile'));
const Inbox = React.lazy(() => import('./components/Inbox'));
const TaxReport = React.lazy(() => import('./components/TaxReport'));
const MyReviews = React.lazy(() => import('./components/MyReviews'));
const AdminVerifications = React.lazy(() => import('./components/AdminVerifications').then(m => ({ default: m.AdminVerifications })));
const ResetPassword = React.lazy(() => import('./components/ResetPassword'));
const BlogList = React.lazy(() => import('./components/Blog'));
const BlogArticle = React.lazy(() => import('./components/BlogArticle'));
const MatchQuiz = React.lazy(() => import('./components/MatchQuiz'));
const RenovationGame = React.lazy(() => import('./components/RenovationGame'));
const HelpWidget = React.lazy(() => import('./components/HelpWidget'));
const CommissionCalculator = React.lazy(() => import('./components/CommissionCalculator').then(m => ({ default: m.CommissionCalculator })));
const ReviewPromptModal = React.lazy(() => import('./components/ReviewPromptModal'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Suspense loading fallback for lazy-loaded routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700 mx-auto mb-3"></div>
      <p className="text-gray-500 text-sm">Зареждане...</p>
    </div>
  </div>
);

// Note: Auth Context moved to separate file /contexts/AuthContext.js

// Landing Page Component
const LandingPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth(); // Check if user is logged in
  const navigate = useNavigate();
  const location = useLocation();
  const [showAuth, setShowAuth] = useState(false);
  const [userCapabilities, setUserCapabilities] = useState(null);
  const [currentMode, setCurrentMode] = useState('client');

  // Fetch user capabilities when user is logged in
  useEffect(() => {
    if (user) {
      fetchUserCapabilities();
    }
  }, [user]);

  // Check for mode change from navigation state
  useEffect(() => {
    if (location.state?.mode) {
      setCurrentMode(location.state.mode);
    }
    if (location.state?.openAuth || location.state?.showAuth) {
      setShowAuth(true);
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const fetchUserCapabilities = async () => {
    try {
      const response = await axios.get(`${API}/auth/user-capabilities`);
      setUserCapabilities(response.data);
      setCurrentMode(response.data.current_mode || 'client');
    } catch (error) {
      console.error('Failed to fetch capabilities:', error);
    }
  };

  // If user is logged in, show mode-specific home page
  if (user) {
    return (
      <ColorfulBackground>
        <div className="min-h-screen">
          {/* Use SharedHeader component */}
          <SharedHeader showBackButton={false} />

          {/* Mode-specific content */}
          {currentMode === 'specialist' ? (
            // SPECIALIST MODE - Calendar & Appointments Focus
            <section className="px-6 py-20">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h1 className="text-4xl font-bold mb-4" style={{ color: '#2D5016' }}>
                    {t('specialistDashboard')}
                  </h1>
                  <p className="text-xl mb-8" style={{ color: '#4A6B2A' }}>
                    {t('welcomeBack')}, {user.name}! {t('manageYourServicesAndAppointments')}
                  </p>
                </div>

                {/* Quick Actions for Specialists */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <Card className="text-center hover:shadow-lg transition-all cursor-pointer" 
                        onClick={() => navigate('/specialist-dashboard')}>
                    <CardContent className="p-8">
                      <div className="text-4xl mb-4">📅</div>
                      <h3 className="text-xl font-bold mb-2" style={{ color: '#2D5016' }}>{t('myCalendar')}</h3>
                      <p className="text-gray-600">{t('viewAndManageAppointments')}</p>
                    </CardContent>
                  </Card>

                  <Card className="text-center hover:shadow-lg transition-all cursor-pointer"
                        onClick={() => navigate('/specialist-dashboard')}>
                    <CardContent className="p-8">
                      <div className="text-4xl mb-4">⚙️</div>
                      <h3 className="text-xl font-bold mb-2" style={{ color: '#2D5016' }}>{t('manageProfile')}</h3>
                      <p className="text-gray-600">{t('updateServicesAndRates')}</p>
                    </CardContent>
                  </Card>

                  <Card className="text-center hover:shadow-lg transition-all cursor-pointer"
                        onClick={() => navigate('/specialist-dashboard')}>
                    <CardContent className="p-8">
                      <div className="text-4xl mb-4">💰</div>
                      <h3 className="text-xl font-bold mb-2" style={{ color: '#2D5016' }}>{t('earnings')}</h3>
                      <p className="text-gray-600">{t('trackIncomeAndPayments')}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Go to Full Specialist Dashboard */}
                <div className="text-center">
                  <Button 
                    size="lg"
                    onClick={() => navigate('/specialist-dashboard')}
                    className="text-white text-lg px-8 py-4 shadow-lg"
                    style={{ background: 'linear-gradient(135deg, #2D5016 0%, #4A6B2A 100%)' }}
                  >
                    {t('openFullSpecialistDashboard')}
                  </Button>
                </div>
              </div>
            </section>
          ) : (
            // CLIENT MODE - Find Services Focus  
            <section className="px-6 py-20">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h1 className="text-4xl font-bold mb-4" style={{ color: '#2D5016' }}>
                    🔍 {t('findServices')}
                  </h1>
                  <p className="text-xl mb-8" style={{ color: '#4A6B2A' }}>
                    {t('welcomeBack')}, {user.name}! {t('whatServiceDoYouNeed')}
                  </p>
                </div>

                {/* Quick Service Categories */}
                <div className="grid md:grid-cols-4 gap-6 mb-8">
                  {[
                    { nameKey: 'houseCleaning', icon: '🏠', category: 'cleaning' },
                    { nameKey: 'handyman', icon: '🔧', category: 'handyman' }, 
                    { nameKey: 'babysitting', icon: '👶', category: 'babysitting' },
                    { nameKey: 'others', icon: '✨', category: 'others' }
                  ].map((service) => (
                    <Card key={service.category} className="text-center hover:shadow-lg transition-all cursor-pointer"
                          onClick={() => navigate(`/auth-services?category=${service.category}`)}>
                      <CardContent className="p-6">
                        <div className="text-3xl mb-3">{service.icon}</div>
                        <h3 className="font-bold" style={{ color: '#2D5016' }}>{t(service.nameKey)}</h3>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Main Action Buttons */}
                <div className="text-center mb-8 space-y-6">
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      size="lg"
                      onClick={() => navigate('/original-app')}
                      className="text-white text-lg px-8 py-4 shadow-lg"
                      style={{ background: 'linear-gradient(135deg, #2D5016 0%, #4A6B2A 100%)' }}
                    >
                      {t('browseBrowseAllServicesAndSpecialists')}
                    </Button>
                    
                    <Button 
                      size="lg"
                      onClick={() => navigate('/simple-map-flow')}
                      className="text-white text-lg px-8 py-4 shadow-lg"
                      style={{ background: 'linear-gradient(135deg, #1D4ED8 0%, #3B82F6 100%)' }}
                    >
                      {t('findSpecialistsOnMap')}
                    </Button>
                  </div>
                  
                  <p className="text-sm text-gray-600 max-w-md mx-auto">
                    {t('exploreNearbySpecialists')}
                  </p>
                </div>

                {/* Quick Links */}
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="hover:shadow-lg transition-all cursor-pointer"
                        onClick={() => navigate('/client-dashboard')}>
                    <CardContent className="p-6 text-center">
                      <div className="text-3xl mb-3">📅</div>
                      <h3 className="font-bold mb-2" style={{ color: '#2D5016' }}>{t('myBookings')}</h3>
                      <p className="text-gray-600">{t('viewAppointments')}</p>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-lg transition-all cursor-pointer"
                        onClick={() => navigate('/settings')}>
                    <CardContent className="p-6 text-center">
                      <div className="text-3xl mb-3">⭐</div>
                      <h3 className="font-bold mb-2" style={{ color: '#2D5016' }}>{t('reviewsAndProfile')}</h3>
                      <p className="text-gray-600">{t('manageAccountSettings')}</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </section>
          )}

          {/* Footer */}
          <footer className="bg-white/70 backdrop-blur-sm border-t border-gray-200 py-6 mt-16">
            <div className="max-w-6xl mx-auto px-6">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-2">
                  <SkillSOSLogo size="sm" />
                  <span className="text-sm text-gray-600">© 2024 {t('skillsos')}. {t('allRightsReserved')}</span>
                </div>
                <div className="flex items-center gap-4">
                  <a href="/blog" className="text-sm text-orange-600 hover:text-orange-700 font-medium">Блог</a>
                  <LegalLinks />
                </div>
              </div>
            </div>
          </footer>
        </div>
      </ColorfulBackground>
    );
  }

  // Public landing page for non-logged-in users
  return (
    <ColorfulBackground>
      <div className="min-h-screen">
        {/* Use SharedHeader component */}
        <SharedHeader showBackButton={false} />

      {/* Hero Section */}
      <section className="px-6 py-20 text-center">
        <div className="max-w-4xl mx-auto relative">
          <div className="absolute -top-10 -left-10 w-20 h-20 bg-yellow-300 rounded-full opacity-20 animate-bounce"></div>
          <div className="absolute -top-5 -right-5 w-16 h-16 bg-pink-300 rounded-full opacity-20 animate-pulse"></div>
          <div className="absolute bottom-10 left-10 w-12 h-12 bg-green-300 rounded-full opacity-20 animate-bounce delay-75"></div>
          
          <h1 className="text-5xl font-bold mb-6" style={{ color: '#2D5016' }}>
            {t('heroTitle')}
          </h1>
          <p className="text-xl mb-8 max-w-2xl mx-auto" style={{ color: '#4A6B2A' }}>
            {t('heroDescription')}
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Button 
              size="lg" 
              onClick={() => navigate('/services')}
              className="text-lg px-8 py-3 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all text-white"
              data-testid="find-services-btn"
              style={{ background: 'linear-gradient(135deg, #2D5016 0%, #4A6B2A 100%)' }}
            >
              {t('findServices')}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              onClick={() => setShowAuth(true)}
              className="text-lg px-8 py-3 border-2 shadow-lg transform hover:scale-105 transition-all"
              data-testid="become-specialist-btn"
              style={{ 
                borderColor: '#2D5016', 
                color: '#2D5016',
                backgroundColor: 'rgba(255,255,255,0.9)'
              }}
            >
              {t('becomeASpecialist')}
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-6 py-16 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-12">
            {t('howSkillSOSWorks')}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center hover:shadow-xl transition-all duration-300 transform hover:scale-105 border-2 border-blue-200 hover:border-blue-400">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <span className="text-3xl">🔍</span>
                </div>
                <CardTitle className="text-blue-600">{t('searchAndDiscover')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {t('searchDescription')}
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-all duration-300 transform hover:scale-105 border-2 border-green-200 hover:border-green-400">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <span className="text-3xl">📅</span>
                </div>
                <CardTitle className="text-green-600">{t('bookAndSchedule')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {t('bookDescription')}
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-all duration-300 transform hover:scale-105 border-2 border-purple-200 hover:border-purple-400">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <span className="text-3xl">💳</span>
                </div>
                <CardTitle className="text-purple-600">{t('securePayment')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {t('paymentDescription')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="px-6 py-16 bg-gradient-to-br from-pink-50 via-blue-50 to-green-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent mb-12">
            {t('popularServices')}
          </h2>
          <div className="grid md:grid-cols-6 sm:grid-cols-2 gap-6">
            {[
              { key: 'houseCleaning', icon: '🏠', color: 'from-blue-400 to-blue-600', border: 'border-blue-200 hover:border-blue-400' },
              { key: 'babysitting', icon: '👶', color: 'from-pink-400 to-pink-600', border: 'border-pink-200 hover:border-pink-400' },
              { key: 'handyman', icon: '🔧', color: 'from-orange-400 to-orange-600', border: 'border-orange-200 hover:border-orange-400' },
              { key: 'petSitting', icon: '🐕', color: 'from-green-400 to-green-600', border: 'border-green-200 hover:border-green-400' },
              { key: 'tutoring', icon: '📚', color: 'from-purple-400 to-purple-600', border: 'border-purple-200 hover:border-purple-400' },
              { key: 'gardening', icon: '🌱', color: 'from-emerald-400 to-emerald-600', border: 'border-emerald-200 hover:border-emerald-400' },
              { key: 'movingHelp', icon: '📦', color: 'from-indigo-400 to-indigo-600', border: 'border-indigo-200 hover:border-indigo-400' },
              { key: 'personalChef', icon: '👨‍🍳', color: 'from-red-400 to-red-600', border: 'border-red-200 hover:border-red-400' },
              { key: 'personalTraining', icon: '💪', color: 'from-yellow-400 to-yellow-600', border: 'border-yellow-200 hover:border-yellow-400' },
              { key: 'beautyServices', icon: '💄', color: 'from-rose-400 to-rose-600', border: 'border-rose-200 hover:border-rose-400' },
              { key: 'others', icon: '✨', color: 'from-gray-400 to-gray-600', border: 'border-gray-200 hover:border-gray-400' }
            ].map((service, index) => (
              <Card key={index} className={`hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-105 border-2 ${service.border} bg-white/80 backdrop-blur-sm`}>
                <CardContent className="p-6 text-center">
                  <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg`}>
                    <span className="text-3xl">{service.icon}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm">{t(service.key)}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Auth Modal */}
      <AuthModal open={showAuth} onOpenChange={setShowAuth} onSuccess={() => {
        const redirectUrl = localStorage.getItem('redirectAfterLogin');
        const redirectTs = parseInt(localStorage.getItem('redirectAfterLogin_ts') || '0');
        localStorage.removeItem('redirectAfterLogin');
        localStorage.removeItem('redirectAfterLogin_ts');
        if (redirectUrl && (Date.now() - redirectTs) < 300000) {
          navigate(redirectUrl, { replace: true });
        }
      }} />
      
      {/* Footer */}
      <footer className="bg-white/70 backdrop-blur-sm border-t border-gray-200 py-6 mt-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <SkillSOSLogo size="sm" />
              <span className="text-sm text-gray-600">© 2024 {t('skillsos')}. {t('allRightsReserved')}</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="/blog" className="text-sm text-orange-600 hover:text-orange-700 font-medium">Блог</a>
              <LegalLinks />
            </div>
          </div>
        </div>
      </footer>
      </div>
    </ColorfulBackground>
  );
};

// Note: AuthModal component moved to separate file /components/AuthModal.js

// Google OAuth Callback Handler
const AuthCallback = () => {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const sessionId = params.get('session_id');

      if (sessionId) {
        try {
          const response = await axios.get(`${API}/auth/session-data`, {
            headers: { 'X-Session-ID': sessionId }
          });

          // Set user data via loginWithToken (stores token + user in localStorage)
          loginWithToken(response.data.access_token, response.data.user);
          window.history.replaceState({}, document.title, window.location.pathname);

          // Check if there's a stored redirect URL from before login
          const redirectUrl = localStorage.getItem('redirectAfterLogin');
          const redirectTs = parseInt(localStorage.getItem('redirectAfterLogin_ts') || '0');
          localStorage.removeItem('redirectAfterLogin');
          localStorage.removeItem('redirectAfterLogin_ts');
          if (redirectUrl && (Date.now() - redirectTs) < 300000) {
            navigate(redirectUrl, { replace: true });
          } else {
            navigate('/home', { replace: true });
          }
          
          toast.success('Успешен вход!');
        } catch (error) {
          console.error('OAuth callback error:', error);
          toast.error('Неуспешна автентикация');
          navigate('/home', { replace: true });
        }
      } else {
        navigate('/home', { replace: true });
      }
    };

    handleCallback();
  }, [loginWithToken, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Влизане...</p>
      </div>
    </div>
  );
};

// Client Dashboard Component
const ClientDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('services'); // Default to services for clients
  const [specialists, setSpecialists] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [searchParams, setSearchParams] = useState({
    service_category: '',
    location: null
  });

  useEffect(() => {
    fetchServices();
    fetchBookings();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to load services');
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to load bookings');
    }
  };

  const searchSpecialists = async () => {
    try {
      const response = await axios.post(`${API}/search/specialists`, searchParams);
      setSpecialists(response.data);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SharedHeader showBackButton={false} />
      
      <div className="max-w-7xl mx-auto px-6 py-6" data-testid="client-dashboard">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('welcomeBack')}, {user?.name}</h1>
          <p className="text-gray-600">{t('findAndBookTrusted')}</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="services" data-testid="services-tab">🔍 {t('findServices')}</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="bookings-tab" className="relative">
              {t('myBookings')}
              {bookings.length > 0 && (
                <Badge 
                  className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5"
                  style={{ fontSize: '10px', minWidth: '18px', height: '18px' }}
                >
                  {bookings.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="reviews" data-testid="reviews-tab">{t('reviews')}</TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">{t('settings')}</TabsTrigger>
          </TabsList>

          <TabsContent value="services">
            {/* Embedded Services Page for Clients */}
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-4" style={{ color: '#2D5016' }}>
                  {t('findServices')}
                </h2>
                <p className="text-lg" style={{ color: '#4A6B2A' }}>
                  Browse trusted specialists and book the services you need
                </p>
              </div>

              {/* Quick Bookings Overview */}
              {bookings.length > 0 && (
                <Card className="mb-6" style={{ borderColor: '#2D5016', backgroundColor: 'rgba(45, 80, 22, 0.05)' }}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between" style={{ color: '#2D5016' }}>
                      <span>📅 Your Upcoming Appointments</span>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setActiveTab('bookings')}
                        style={{ borderColor: '#2D5016', color: '#2D5016' }}
                      >
                        View All ({bookings.length})
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {bookings.slice(0, 2).map(booking => (
                        <div key={booking.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div>
                            <p className="font-medium" style={{ color: '#2D5016' }}>
                              {booking.specialist_name} - {booking.service_category}
                            </p>
                            <p className="text-sm text-gray-600">
                              {booking.date} at {booking.time_slot}
                            </p>
                          </div>
                          <Badge variant="outline" style={{ borderColor: '#2D5016', color: '#2D5016' }}>
                            {booking.status}
                          </Badge>
                        </div>
                      ))}
                      {bookings.length > 2 && (
                        <p className="text-center text-sm text-gray-500 pt-2">
                          +{bookings.length - 2} more appointments
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Service Search and Browse */}
              <EnhancedSearchSpecialists 
                services={services}
                specialists={specialists}
                setSpecialists={setSpecialists}
              />
              
              {/* Direct Link to Full Services Page */}
              <Card className="text-center py-8" style={{ borderColor: '#2D5016', backgroundColor: 'rgba(45, 80, 22, 0.05)' }}>
                <CardContent>
                  <h3 className="text-xl font-bold mb-4" style={{ color: '#2D5016' }}>
                    Browse All Available Services
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Explore our complete marketplace with detailed specialist profiles
                  </p>
                  <Button 
                    size="lg"
                    onClick={() => window.open('/services', '_blank')}
                    className="text-white text-lg px-8 py-3 shadow-lg"
                    style={{ background: 'linear-gradient(135deg, #2D5016 0%, #4A6B2A 100%)' }}
                  >
                    🔍 View Full Services Marketplace
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="bookings">
            <ClientBookingsCalendar bookings={bookings} onUpdate={fetchBookings} />
          </TabsContent>

          <TabsContent value="reviews">
            <div className="text-center py-8">
              <p className="text-gray-500">Review system coming soon</p>
            </div>
          </TabsContent>

          <TabsContent value="become-specialist">
            <BecomeSpecialistSection onUpdate={() => window.location.reload()} />
          </TabsContent>

          <TabsContent value="settings">
            <ClientSettings user={user} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Specialist Dashboard Component  
const SpecialistDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetchServices();
    fetchProfile();
    fetchBookings();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to load services');
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/specialist/profile`);
      setProfile(response.data);
    } catch (error) {
      // Profile doesn't exist yet
      setProfile(null);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to load bookings');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SharedHeader showBackButton={false} />
      
      <div className="max-w-7xl mx-auto px-6 py-6" data-testid="specialist-dashboard">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('specialist')} Dashboard</h1>
          <p className="text-gray-600">{t('manageServicesAndBookings')}</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="profile" data-testid="profile-tab">🏆 {t('becomeSpecialist')}</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="bookings-tab">{t('bookings')}</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="calendar-tab">{t('availability')}</TabsTrigger>
            <TabsTrigger value="earnings" data-testid="earnings-tab">{t('earnings')}</TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">{t('settings')}</TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <SpecialistProfile 
              profile={profile}
              services={services}
              onUpdate={fetchProfile}
            />
          </TabsContent>

          <TabsContent value="bookings">
            <SpecialistBookings bookings={bookings} onUpdate={fetchBookings} />
          </TabsContent>

          <TabsContent value="calendar">
            <EnhancedAvailabilityManager />
          </TabsContent>

          <TabsContent value="earnings">
            <div className="text-center py-8">
              <p className="text-gray-500">Earnings dashboard coming soon</p>
            </div>
          </TabsContent>

          <TabsContent value="settings">
            <SpecialistSettings user={user} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Simple Role Switcher Component
const SimpleRoleSwitcher = ({ user }) => {
  const { t } = useTranslation();
  const [switching, setSwitching] = useState(false);
  const [currentMode, setCurrentMode] = useState('client');
  const [userCapabilities, setUserCapabilities] = useState(null);
  const navigate = useNavigate();

  // Fetch user capabilities and set current mode
  useEffect(() => {
    if (user) {
      fetchUserCapabilities();
    }
  }, [user]);

  const fetchUserCapabilities = async () => {
    try {
      const response = await axios.get(`${API}/auth/user-capabilities`);
      setUserCapabilities(response.data);
      setCurrentMode(response.data.current_mode || 'client');
    } catch (error) {
      console.error('Failed to fetch user capabilities:', error);
      setCurrentMode(user?.user_type || 'client');
    }
  };

  const handleModeSwitch = async (newMode) => {
    setSwitching(true);
    try {
      const response = await axios.post(`${API}/auth/switch-mode`, null, {
        params: { new_mode: newMode }
      });
      
      // Update local state immediately
      setCurrentMode(newMode);
      
      toast.success(`${t('switchTo')} ${t(newMode)} ${t('mode')}!`);
      
      // Stay on home page but trigger a re-render with new mode
      // Don't navigate away to preserve authentication
      navigate('/', { replace: true, state: { mode: newMode } });
      
      // Refresh capabilities to sync with backend
      await fetchUserCapabilities();
      
    } catch (error) {
      toast.error('Failed to switch mode');
      console.error('Mode switch error:', error);
      // Reset to previous mode on error
      await fetchUserCapabilities();
    }
    setSwitching(false);
  };

  if (!user) return null;

  return (
    <div className="flex items-center gap-2">
      {/* Current Mode Badge */}
      <Badge 
        variant={currentMode === 'specialist' ? 'default' : 'secondary'}
        className="text-xs font-medium"
      >
        {currentMode === 'specialist' ? `🔧 ${t('specialist')}` : `👤 ${t('client')}`}
      </Badge>

      {/* Switch Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleModeSwitch(currentMode === 'client' ? 'specialist' : 'client')}
        disabled={switching}
        className="text-xs border-blue-500 text-blue-600 hover:bg-blue-500 hover:text-white transition-all"
        data-testid="role-switch-btn"
      >
        {switching ? (
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-3 w-3 border border-current border-t-transparent"></div>
            Switching...
          </div>
        ) : (
          `${t('switchTo')} ${t(currentMode === 'client' ? 'specialist' : 'client')}`
        )}
      </Button>
    </div>
  );
};

// Shared Dashboard Header
const DashboardHeader = ({ user }) => {
  const { t } = useTranslation();
  const [capabilities, setCapabilities] = useState(null);

  useEffect(() => {
    fetchCapabilities();
  }, []);

  const fetchCapabilities = async () => {
    try {
      const response = await axios.get(`${API}/auth/user-capabilities`);
      setCapabilities(response.data);
    } catch (error) {
      console.error('Failed to fetch capabilities:', error);
    }
  };

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
        <SkillSOSLogo size="md" />
        <div className="flex items-center gap-3">
          <CurrencySelector />
          
          {/* Simple Always-Visible Role Switcher */}
          <SimpleRoleSwitcher user={user} />
          
          {/* User Dropdown */}
          <UserDropdown user={user} capabilities={capabilities} />
        </div>
      </div>
    </header>
  );
};

// Enhanced Search Specialists Component
const EnhancedSearchSpecialists = ({ services, specialists, setSpecialists }) => {
  const handleResults = (results) => {
    setSpecialists(results);
  };

  return (
    <div className="space-y-6">
      <EnhancedSearch 
        services={services}
        onResults={handleResults}
      />

      {specialists.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">
            Found {specialists.length} specialists
          </h3>
          <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-4">
            {specialists.map(specialist => (
              <EnhancedSpecialistCard 
                key={specialist.id} 
                specialist={specialist}
                onBook={(specialist) => {
                  // Handle booking - could open booking modal
                  console.log('Book specialist:', specialist);
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Search Specialists Component (Original)
const SearchSpecialists = ({ services, specialists, searchParams, setSearchParams, onSearch }) => {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Find Service Providers</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Service Type</Label>
              <Select 
                value={searchParams.service_category} 
                onValueChange={(value) => setSearchParams({...searchParams, service_category: value})}
              >
                <SelectTrigger data-testid="service-select">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map(service => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location</Label>
              <Input 
                placeholder="Enter your location"
                data-testid="location-input"
              />
            </div>
          </div>
          <Button onClick={onSearch} className="w-full" data-testid="search-button">
            Search Specialists
          </Button>
        </CardContent>
      </Card>

      {specialists.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {specialists.map(specialist => (
            <SpecialistCard key={specialist.id} specialist={specialist} />
          ))}
        </div>
      )}
    </div>
  );
};

// Specialist Card Component
const SpecialistCard = ({ specialist }) => {
  const [showBookingModal, setShowBookingModal] = useState(false);

  return (
    <>
      <Card className="hover:shadow-lg transition-shadow">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Avatar>
              <AvatarImage src={specialist.profile_image} />
              <AvatarFallback>{specialist.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">{specialist.name}</h3>
              <p className="text-gray-600 text-sm mb-2">{specialist.bio}</p>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-yellow-500">★</span>
                <span className="text-sm">{specialist.rating.toFixed(1)} ({specialist.total_reviews} reviews)</span>
              </div>
              <p className="font-semibold text-blue-600">${specialist.hourly_rate}/hour</p>
            </div>
          </div>
          <Button 
            className="w-full mt-4" 
            onClick={() => setShowBookingModal(true)}
            data-testid={`book-specialist-${specialist.id}`}
          >
            Book Now
          </Button>
        </CardContent>
      </Card>
      
      <BookingModal 
        specialist={specialist}
        open={showBookingModal}
        onOpenChange={setShowBookingModal}
      />
    </>
  );
};

// Booking Modal Component
const BookingModal = ({ specialist, open, onOpenChange }) => {
  const [bookingData, setBookingData] = useState({
    date: '',
    time_slot: '',
    duration_hours: 2,
    address: '',
    special_instructions: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/bookings`, {
        ...bookingData,
        specialist_id: specialist.user_id,
        service_category: specialist.services[0] // Use first service
      });

      toast.success('Booking created successfully!');
      onOpenChange(false);
      
      // Navigate to payment
      window.location.href = `/payment/${response.data.booking_id}`;
    } catch (error) {
      toast.error('Booking failed');
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Book {specialist.name}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={bookingData.date}
                onChange={(e) => setBookingData({...bookingData, date: e.target.value})}
                required
                data-testid="booking-date"
              />
            </div>
            <div>
              <Label>Time</Label>
              <Select 
                value={bookingData.time_slot}
                onValueChange={(value) => setBookingData({...bookingData, time_slot: value})}
              >
                <SelectTrigger data-testid="booking-time">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'].map(time => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Duration (hours)</Label>
            <Select 
              value={bookingData.duration_hours.toString()}
              onValueChange={(value) => setBookingData({...bookingData, duration_hours: parseInt(value)})}
            >
              <SelectTrigger data-testid="booking-duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 8].map(hours => (
                  <SelectItem key={hours} value={hours.toString()}>{hours} hour{hours > 1 ? 's' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Service Address</Label>
            <Input
              value={bookingData.address}
              onChange={(e) => setBookingData({...bookingData, address: e.target.value})}
              placeholder="Enter service address"
              required
              data-testid="booking-address"
            />
          </div>

          <div>
            <Label>Special Instructions</Label>
            <Textarea
              value={bookingData.special_instructions}
              onChange={(e) => setBookingData({...bookingData, special_instructions: e.target.value})}
              placeholder="Any special requirements or notes"
              data-testid="booking-instructions"
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center">
              <span>Total Cost:</span>
              <span className="font-bold text-lg">
                ${(specialist.hourly_rate * bookingData.duration_hours).toFixed(2)}
              </span>
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={loading} data-testid="confirm-booking">
            {loading ? 'Creating Booking...' : 'Confirm Booking'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Become Specialist Section
const BecomeSpecialistSection = ({ onUpdate }) => {
  const [loading, setLoading] = useState(false);

  const handleBecomeSpecialist = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/switch-mode`, null, {
        params: { new_mode: 'specialist' }
      });
      
      toast.success('Welcome! You can now provide services as a specialist.');
      onUpdate();
    } catch (error) {
      toast.error('Failed to activate specialist mode');
    }
    setLoading(false);
  };

  return (
    <Card>
      <CardContent className="text-center py-12">
        <div className="max-w-md mx-auto">
          <div className="text-6xl mb-6">🔧</div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Start Earning as a Specialist
          </h3>
          <p className="text-gray-600 mb-6">
            Join thousands of service providers earning money by offering your skills. 
            Set your own rates, manage your schedule, and grow your business.
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-2">
              <span className="text-green-600">✓</span>
              <span className="text-sm">Set your own hourly rates</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <span className="text-green-600">✓</span>
              <span className="text-sm">Manage your own schedule</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <span className="text-green-600">✓</span>
              <span className="text-sm">Secure payment processing</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <span className="text-green-600">✓</span>
              <span className="text-sm">Build your reputation with reviews</span>
            </div>
          </div>
          <Button 
            onClick={handleBecomeSpecialist}
            disabled={loading}
            size="lg"
            className="mt-6 bg-blue-600 hover:bg-blue-700"
            data-testid="become-specialist-btn"
          >
            {loading ? 'Activating...' : 'Become a Specialist'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Client Settings Component
const ClientSettings = ({ user }) => {
  const [consent, setConsent] = useState({
    terms_accepted: false,
    privacy_accepted: false,
    marketing_consent: false
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConsent();
  }, []);

  const fetchConsent = async () => {
    try {
      const response = await axios.get(`${API}/legal/consent`);
      setConsent(response.data);
    } catch (error) {
      console.error('Failed to fetch consent:', error);
    }
  };

  const updateConsent = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/legal/consent/update`, null, {
        params: consent
      });
      toast.success('Consent preferences updated successfully');
    } catch (error) {
      toast.error('Failed to update consent preferences');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">⚙️</span>
            {t('settings') || 'Настройки'}
          </CardTitle>
          <CardDescription>
            {t('manageAccountSettings') || 'Управление на акаунта и правни настройки'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Account Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Информация за акаунта</h3>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Име:</span>
                <span className="font-medium">{user?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Имейл:</span>
                <span className="font-medium">{user?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Телефон:</span>
                <span className="font-medium">{user?.phone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Тип акаунт:</span>
                <Badge variant="outline">{user?.user_type}</Badge>
              </div>
            </div>
          </div>

          {/* Legal Consent Management */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Поверителност и съгласие</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">Marketing Communications</p>
                  <p className="text-sm text-gray-600">Receive promotional emails and updates</p>
                </div>
                <input
                  type="checkbox"
                  checked={consent.marketing_consent}
                  onChange={(e) => setConsent({...consent, marketing_consent: e.target.checked})}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>
              
              <Button 
                onClick={updateConsent} 
                disabled={loading}
                className="w-full"
                data-testid="update-consent-btn"
              >
                {loading ? 'Updating...' : 'Update Preferences'}
              </Button>
            </div>
          </div>

          {/* Legal Documents */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Правни документи</h3>
            <div className="grid grid-cols-2 gap-4">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="h-auto p-4 flex-col">
                    <span className="text-2xl mb-2">📄</span>
                    <span>Условия за ползване</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[80vh]">
                  <DialogHeader>
                    <DialogTitle>Условия за ползване</DialogTitle>
                  </DialogHeader>
                  <TermsAndConditions inModal={true} />
                </DialogContent>
              </Dialog>

              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="h-auto p-4 flex-col">
                    <span className="text-2xl mb-2">🔒</span>
                    <span>Политика за поверителност</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[80vh]">
                  <DialogHeader>
                    <DialogTitle>Политика за поверителност</DialogTitle>
                  </DialogHeader>
                  <PrivacyPolicy inModal={true} />
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Поддръжка</h3>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700">
                За въпроси относно поверителността или Вашия акаунт, свържете се с нас:
              </p>
              <div className="mt-2 space-y-1">
                <p className="text-sm font-medium text-blue-900">admin@skillsos.bg</p>
                <p className="text-sm text-blue-700">Време за отговор: 24-48 часа</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Specialist Settings Component  
const SpecialistSettings = ({ user }) => {
  const [consent, setConsent] = useState({
    terms_accepted: false,
    privacy_accepted: false,
    marketing_consent: false
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConsent();
  }, []);

  const fetchConsent = async () => {
    try {
      const response = await axios.get(`${API}/legal/consent`);
      setConsent(response.data);
    } catch (error) {
      console.error('Failed to fetch consent:', error);
    }
  };

  const updateConsent = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/legal/consent/update`, null, {
        params: consent
      });
      toast.success('Consent preferences updated successfully');
    } catch (error) {
      toast.error('Failed to update consent preferences');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">⚙️</span>
            Настройки на специалист
          </CardTitle>
          <CardDescription>
            Управление на акаунта и правни настройки
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Account Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Информация за акаунта</h3>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Име:</span>
                <span className="font-medium">{user?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Имейл:</span>
                <span className="font-medium">{user?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Телефон:</span>
                <span className="font-medium">{user?.phone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Account Type:</span>
                <Badge variant="outline">{user?.user_type}</Badge>
              </div>
            </div>
          </div>

          {/* Privacy & Consent Management */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Privacy & Consent</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">Marketing Communications</p>
                  <p className="text-sm text-gray-600">Receive promotional emails and business updates</p>
                </div>
                <input
                  type="checkbox"
                  checked={consent.marketing_consent}
                  onChange={(e) => setConsent({...consent, marketing_consent: e.target.checked})}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>
              
              <Button 
                onClick={updateConsent} 
                disabled={loading}
                className="w-full"
                data-testid="update-specialist-consent-btn"
              >
                {loading ? 'Updating...' : 'Update Preferences'}
              </Button>
            </div>
          </div>

          {/* Legal Documents */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Правни документи</h3>
            <div className="grid grid-cols-2 gap-4">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="h-auto p-4 flex-col">
                    <span className="text-2xl mb-2">📄</span>
                    <span>Условия за ползване</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[80vh]">
                  <DialogHeader>
                    <DialogTitle>Условия за ползване</DialogTitle>
                  </DialogHeader>
                  <TermsAndConditions inModal={true} />
                </DialogContent>
              </Dialog>

              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="h-auto p-4 flex-col">
                    <span className="text-2xl mb-2">🔒</span>
                    <span>Политика за поверителност</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[80vh]">
                  <DialogHeader>
                    <DialogTitle>Политика за поверителност</DialogTitle>
                  </DialogHeader>
                  <PrivacyPolicy inModal={true} />
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Specialist-specific settings */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Бизнес настройки</h3>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-yellow-700 mb-2">
                <span className="font-medium">Важно:</span> Като доставчик на услуги, Вие имате допълнителни отговорности съгласно законите за защита на потребителите.
              </p>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Provide accurate service descriptions</li>
                <li>• Honor booking commitments</li>
                <li>• Maintain professional standards</li>
              </ul>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Поддръжка</h3>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700">
                За бизнес въпроси или въпроси относно поверителността:
              </p>
              <div className="mt-2 space-y-1">
                <p className="text-sm font-medium text-blue-900">admin@skillsos.bg</p>
                <p className="text-sm font-medium text-blue-900">business@skillsos.com</p>
                <p className="text-sm text-blue-700">Време за отговор: 24-48 часа</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Bookings List Component
const BookingsList = ({ bookings, onUpdate }) => {
  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status`, null, {
        params: { status }
      });
      toast.success('Booking updated');
      onUpdate();
    } catch (error) {
      toast.error('Update failed');
    }
  };

  return (
    <div className="space-y-4">
      {bookings.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-gray-500">No bookings yet</p>
          </CardContent>
        </Card>
      ) : (
        bookings.map(booking => (
          <Card key={booking.id}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-lg">
                    {booking.specialist_name || booking.client_name}
                  </h3>
                  <p className="text-gray-600">{booking.service_category}</p>
                  <p className="text-sm text-gray-500">
                    {booking.date} at {booking.time_slot} ({booking.duration_hours}h)
                  </p>
                  <p className="text-sm text-gray-500">{booking.address}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-lg">${booking.total_amount}</p>
                  <Badge 
                    variant={booking.status === 'confirmed' ? 'default' : 'secondary'}
                    data-testid={`booking-status-${booking.id}`}
                  >
                    {booking.status}
                  </Badge>
                </div>
              </div>
              {booking.status === 'pending' && booking.payment_status === 'pending' && (
                <div className="mt-4 flex gap-2">
                  <Button 
                    size="sm"
                    onClick={() => window.location.href = `/payment/${booking.id}`}
                    data-testid={`pay-booking-${booking.id}`}
                  >
                    Pay Now
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                    data-testid={`cancel-booking-${booking.id}`}
                  >
                    Cancel
                  </Button>
                </div>
              )}
              {(booking.status === 'confirmed' || booking.status === 'completed') && (
                <div className="mt-4">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline">
                        💬 Chat with {booking.specialist_name || booking.client_name}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle>
                          Chat - {booking.service_category} on {booking.date}
                        </DialogTitle>
                      </DialogHeader>
                      <ChatSystem 
                        booking={booking}
                        currentUser={{ id: 'current_user_id' }} // This should be actual current user
                      />
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

// Specialist Profile Component
const SpecialistProfile = ({ profile, services, onUpdate }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    services: [],
    hourly_rate: '',
    currency: 'EUR',
    minimum_service_fee: '',
    bio: '',
    experience_years: '',
    location: { address: '', lat: 0, lng: 0 },
    profile_image: null
  });
  const [loading, setLoading] = useState(false);

  const getServiceNameForId = (serviceId) => {
    // First try to get translation key
    const translationKey = serviceId.replace('-', '');
    if (t(translationKey) !== translationKey) {
      return t(translationKey);
    }
    
    // Check direct service ID translations
    if (t(serviceId) !== serviceId) {
      return t(serviceId);
    }
    
    // Fallback to API service name
    const service = services.find(s => s.id === serviceId);
    return service?.name || serviceId;
  };

  useEffect(() => {
    if (profile) {
      setFormData({
        services: profile.services || [],
        hourly_rate: profile.hourly_rate || '',
        currency: profile.currency || 'EUR',
        minimum_service_fee: profile.minimum_service_fee || '',
        bio: profile.bio || '',
        experience_years: profile.experience_years || '',
        location: profile.location || { address: '', lat: 0, lng: 0 },
        profile_image: profile.profile_image || null
      });
    }
  }, [profile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/specialist/profile`, {
        ...formData,
        hourly_rate: parseFloat(formData.hourly_rate),
        minimum_service_fee: formData.minimum_service_fee ? parseFloat(formData.minimum_service_fee) : null,
        experience_years: parseInt(formData.experience_years)
      });
      
      toast.success('Profile saved successfully!');
      onUpdate();
    } catch (error) {
      toast.error('Failed to save profile');
    }
    setLoading(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Specialist Profile</CardTitle>
        <CardDescription>
          Complete your profile to start receiving bookings
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Image Upload */}
          <ImageUpload 
            currentImage={formData.profile_image}
            onImageUploaded={(imageUrl) => {
              setFormData({...formData, profile_image: imageUrl});
            }}
            title={t('profileImage')}
          />

          <div>
            <Label>{t('servicesOffered')}</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {services.map(service => (
                <label key={service.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.services.includes(service.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          services: [...formData.services, service.id]
                        });
                      } else {
                        setFormData({
                          ...formData,
                          services: formData.services.filter(s => s !== service.id)
                        });
                      }
                    }}
                    data-testid={`service-${service.id}`}
                  />
                  <span className="text-sm">{getServiceNameForId(service.id)}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="hourly_rate">{t('hourlyRate')}</Label>
              <Input
                id="hourly_rate"
                type="number"
                step="0.01"
                value={formData.hourly_rate}
                onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                required
                data-testid="hourly-rate"
              />
            </div>
            <div>
              <Label htmlFor="currency">{t('currency')}</Label>
              <Select 
                value={formData.currency} 
                onValueChange={(value) => setFormData({...formData, currency: value})}
              >
                <SelectTrigger data-testid="currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">€ EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="minimum_fee">{t('minimumServiceFee')}</Label>
              <Input
                id="minimum_fee"
                type="number"
                step="0.01"
                value={formData.minimum_service_fee}
                onChange={(e) => setFormData({...formData, minimum_service_fee: e.target.value})}
                placeholder="Optional"
                data-testid="minimum-fee"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="experience_years">{t('yearsOfExperience')}</Label>
              <Input
                id="experience_years"
                type="number"
                value={formData.experience_years}
                onChange={(e) => setFormData({...formData, experience_years: e.target.value})}
                required
                data-testid="experience-years"
              />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-700">
              {t('minimumServiceFeeDescription')}
            </p>
          </div>

          <div>
            <Label htmlFor="bio">{t('bio')}</Label>
            <Textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => setFormData({...formData, bio: e.target.value})}
              placeholder={t('bio')}
              rows={4}
              required
              data-testid="bio"
            />
          </div>

          <div>
            <Label htmlFor="location">Service Area</Label>
            <Input
              id="location"
              value={formData.location.address}
              onChange={(e) => setFormData({
                ...formData,
                location: { ...formData.location, address: e.target.value }
              })}
              placeholder="Enter your service area"
              required
              data-testid="location"
            />
          </div>

          <Button type="submit" className="w-full" disabled={loading} data-testid="save-profile">
            {loading ? 'Saving...' : 'Save Profile'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Specialist Bookings Component
const SpecialistBookings = ({ bookings, onUpdate }) => {
  const { t } = useTranslation();
  
  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status`, null, {
        params: { status }
      });
      toast.success('Booking updated');
      onUpdate();
    } catch (error) {
      toast.error('Update failed');
    }
  };

  // Calculate provider earnings breakdown (5% client fee + 5% provider fee = 10% total to SkillSOS)
  const calculateProviderEarnings = (booking) => {
    const serviceAmount = parseFloat(booking.total_amount) || 0;
    const providerFee = serviceAmount * 0.05; // 5% provider fee to SkillSOS
    const clientPays = serviceAmount * 1.05; // Client pays service + 5%
    const stripeFeeEstimate = (clientPays * 0.015) + 0.25; // Approx Stripe fee
    const providerReceives = serviceAmount - providerFee - stripeFeeEstimate;
    return {
      serviceAmount,
      providerFee,
      stripeFeeEstimate,
      providerReceives
    };
  };

  return (
    <div className="space-y-4">
      {bookings.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-gray-500">{t('noBookingsYet') || 'No bookings yet'}</p>
          </CardContent>
        </Card>
      ) : (
        bookings.map(booking => {
          const earnings = calculateProviderEarnings(booking);
          return (
          <Card key={booking.id}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-lg">{booking.client_name}</h3>
                  <p className="text-gray-600">{booking.service_category}</p>
                  <p className="text-sm text-gray-500">
                    {booking.date} at {booking.time_slot} ({booking.duration_hours}h)
                  </p>
                  <p className="text-sm text-gray-500">{booking.address}</p>
                  {booking.special_instructions && (
                    <p className="text-sm text-gray-700 mt-2">
                      <span className="font-medium">{t('instructions') || 'Instructions'}:</span> {booking.special_instructions}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <Badge 
                    variant={booking.status === 'confirmed' ? 'default' : 'secondary'}
                    data-testid={`booking-status-${booking.id}`}
                    className="mb-2"
                  >
                    {t(booking.status) || booking.status}
                  </Badge>
                  
                  {/* Provider Earnings Breakdown */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3 mt-2 text-left min-w-[180px]">
                    <p className="text-xs text-gray-500 mb-2 font-medium">{t('earningsBreakdown') || 'Вашите приходи'}:</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('serviceAmount') || 'Услуга'}:</span>
                        <span>€{earnings.serviceAmount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <span>{t('platformFee') || 'Такса SkillSOS'} (5%):</span>
                        <span>-€{earnings.providerFee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-gray-400 text-xs">
                        <span>{t('stripeFee') || 'Такса Stripe'} (≈1.5%+€0.25):</span>
                        <span>≈-€{earnings.stripeFeeEstimate.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-green-200 pt-1 mt-1">
                        <div className="flex justify-between font-bold text-green-700">
                          <span>{t('youReceive') || 'Вие получавате'} (≈):</span>
                          <span>≈€{earnings.providerReceives.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {booking.status === 'pending' && (
                <div className="mt-4 flex gap-2">
                  <Button 
                    size="sm"
                    onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                    data-testid={`accept-booking-${booking.id}`}
                  >
                    {t('accept') || 'Accept'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                    data-testid={`reject-booking-${booking.id}`}
                  >
                    {t('decline') || 'Decline'}
                  </Button>
                </div>
              )}
              {booking.status === 'confirmed' && (
                <div className="mt-4">
                  <div className="flex gap-2">
                    <Button 
                      size="sm"
                      onClick={() => updateBookingStatus(booking.id, 'completed')}
                      data-testid={`complete-booking-${booking.id}`}
                    >
                      {t('markCompleted') || 'Mark Completed'}
                    </Button>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline">
                          💬 {t('chat') || 'Chat'}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-lg">
                        <DialogHeader>
                          <DialogTitle>{t('chatWith') || 'Chat with'} {booking.client_name}</DialogTitle>
                        </DialogHeader>
                        <ChatSystem 
                          booking={booking}
                          currentUser={{ id: 'current_user_id' }} // This should be actual current user
                        />
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )})
      )}
    </div>
  );
};

// Availability Manager Component
const AvailabilityManager = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timeSlots, setTimeSlots] = useState([]);

  const availableSlots = [
    '09:00', '10:00', '11:00', '12:00',
    '14:00', '15:00', '16:00', '17:00'
  ];

  const toggleTimeSlot = (slot) => {
    if (timeSlots.includes(slot)) {
      setTimeSlots(timeSlots.filter(s => s !== slot));
    } else {
      setTimeSlots([...timeSlots, slot]);
    }
  };

  const saveAvailability = async () => {
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      await axios.put(`${API}/specialist/availability`, {
        [dateStr]: timeSlots
      });
      toast.success('Availability updated');
    } catch (error) {
      toast.error('Update failed');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Manage Availability</CardTitle>
          <CardDescription>
            Set your available time slots for each day
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium mb-4">Select Date</h3>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="rounded-md border"
                data-testid="availability-calendar"
              />
            </div>
            
            <div>
              <h3 className="font-medium mb-4">
                Available Times for {selectedDate?.toDateString()}
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {availableSlots.map(slot => (
                  <Button
                    key={slot}
                    variant={timeSlots.includes(slot) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleTimeSlot(slot)}
                    data-testid={`time-slot-${slot}`}
                  >
                    {slot}
                  </Button>
                ))}
              </div>
              <Button 
                className="w-full mt-4" 
                onClick={saveAvailability}
                data-testid="save-availability"
              >
                Save Availability
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Payment Success Page
const PaymentSuccess = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState('checking');
  const [bookingDetails, setBookingDetails] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const params = new URLSearchParams(location.search);
      const sessionId = params.get('session_id');
      
      if (sessionId) {
        try {
          const response = await axios.get(`${API}/payments/status/${sessionId}`);
          if (response.data.payment_status === 'paid') {
            setStatus('success');
            // Try to get booking details
            if (response.data.booking_id) {
              setBookingDetails(response.data);
            }
            
            // Fire Meta Pixel "Schedule" event for successful booking
            if (window.fbq) {
              window.fbq('track', 'Schedule', {
                content_name: response.data.service_category || 'Service Booking',
                value: response.data.amount ? response.data.amount / 100 : 0,
                currency: 'EUR'
              });
              console.log('Meta Pixel: Schedule event fired');
            }
          } else {
            setStatus('pending');
          }
        } catch (error) {
          setStatus('error');
        }
      } else {
        setStatus('error');
      }
    };

    checkPaymentStatus();
  }, [location]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('verifyingPayment') || 'Проверка на плащането...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="max-w-lg w-full shadow-lg">
        <CardContent className="text-center py-10">
          {status === 'success' || status === 'pending' ? (
            <>
              {/* Logo */}
              <img src="/skillsos-logo.png" alt="SkillSOS" className="h-16 mx-auto mb-6" />
              
              {/* Success Icon */}
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                {t('paymentSuccessTitle') || 'Плащането е успешно!'}
              </h2>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t('bookingConfirmedAfterPayment') || 'Резервацията ви е потвърдена! Специалистът ще получи адреса и детайлите.'}
              </p>
              
              {/* Status info box */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">✅</span>
                  <div>
                    <h4 className="font-semibold text-green-800 mb-1">
                      {t('bookingConfirmed') || 'Резервацията е потвърдена'}
                    </h4>
                    <p className="text-sm text-green-700">
                      {t('providerNotifiedWithAddress') || 'Специалистът е уведомен и ще получи адреса ви. Очаквайте го на уговорената дата и час.'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* What happens next */}
              <div className="text-left bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-800 mb-3">
                  {t('whatHappensNext') || 'Какво следва?'}
                </h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-0.5">✓</span>
                    <span>{t('step1PaymentHeld') || 'Плащането ви е задържано сигурно'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-0.5">✓</span>
                    <span>{t('step2ProviderNotified') || 'Специалистът е уведомен с адреса и детайлите'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-500 mt-0.5">○</span>
                    <span>{t('step3ServiceCompleted') || 'След извършване на услугата, плащането се освобождава'}</span>
                  </li>
                </ul>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => window.location.href = '/my-profile'}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {t('viewMyBookings') || 'Виж моите резервации'}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => window.location.href = '/'}
                  className="flex-1"
                >
                  {t('backToHome') || 'Към началото'}
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Error state */}
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                {t('paymentFailedTitle') || 'Плащането не успя'}
              </h2>
              
              <p className="text-gray-600 mb-6">
                {t('paymentFailedMessage') || 'Възникна проблем с плащането. Моля, опитайте отново.'}
              </p>
              
              <Button 
                onClick={() => window.location.href = '/'}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {t('tryAgain') || 'Опитайте отново'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredUserType }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!user) {
    // Store the intended destination for redirect after login
    const intendedPath = location.pathname + location.search;
    if (intendedPath !== '/' && intendedPath !== '') {
      localStorage.setItem('redirectAfterLogin', intendedPath);
      localStorage.setItem('redirectAfterLogin_ts', Date.now().toString());
    }
    
    // Redirect to home with state to trigger auth modal
    return <Navigate to="/" state={{ showAuth: true, authMode: 'login', intendedPath }} replace />;
  }

  // Simplified logic: Allow access if user has the required type OR user has 'both' capabilities
  const hasAccess = 
    user.user_type === requiredUserType || 
    user.user_type === 'both';

  if (requiredUserType && !hasAccess) {
    console.log(`Access denied: user_type=${user.user_type}, required=${requiredUserType}`);
    return <Navigate to="/" replace />;
  }

  console.log(`Access granted: user_type=${user.user_type}, required=${requiredUserType}`);
  return children;
};

// Main App Component
function CalcWidgetWrapper() {
  // Commission calculator hidden while cash-only mode is active
  return null;
}

function App() {
  return (
    <CurrencyProvider>
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <AuthProvider>
          <LocationModalWrapper>
            <FooterProvider>
              <InboxProvider>
                <BookingProvider>
                  <WalletProvider>
                    <BrowserRouter>
                      <div className="App">
                        <Suspense fallback={<PageLoader />}>
                        <Routes>
                <Route path="/" element={<Navigate to="/home" replace />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/calculator" element={<Calculator />} />
                <Route path="/coming-soon" element={<ComingSoon />} />
                <Route path="/home" element={<BeautifulHomePage />} />
                <Route path="/admin/wishlist" element={<AdminWishlist />} />
                <Route path="/original-app" element={<LandingPage />} />
                <Route path="/services" element={<PublicServicesShowcase />} />
                <Route path="/specialists" element={<PublicServicesShowcase />} />
                <Route path="/auth-services" element={<Navigate to="/specialists" replace />} />
                {/* Map Flow Routes */}
                <Route path="/map-location" element={<MapLocationSelector />} />
                <Route path="/map-results" element={<MapSearchResults />} />
                <Route path="/simple-map-flow" element={<SimpleMapFlow />} />
                <Route path="/simple-map-results" element={<SimpleMapResults />} />
                <Route path="/maps-demo" element={<GoogleMapsDemo />} />
                <Route path="/specialist/:specialistId" element={<EnhancedSpecialistProfile />} />
                <Route path="/bookings-demo" element={<MockBookingsDemo />} />
                <Route path="/my-bookings" element={
                  <ProtectedRoute>
                    <MyBookings />
                  </ProtectedRoute>
                } />
                <Route path="/my-reviews" element={
                  <ProtectedRoute>
                    <MyReviews />
                  </ProtectedRoute>
                } />
                <Route path="/admin/verifications" element={
                  <ProtectedRoute>
                    <AdminVerifications />
                  </ProtectedRoute>
                } />
                <Route path="/booking-requests" element={
                  <ProtectedRoute>
                    <BookingRequests />
                  </ProtectedRoute>
                } />
                <Route path="/my-profile" element={
                  <ProtectedRoute>
                    <MyProfile />
                  </ProtectedRoute>
                } />
                <Route path="/inbox" element={
                  <ProtectedRoute>
                    <Inbox />
                  </ProtectedRoute>
                } />
                <Route path="/tax-report" element={
                  <ProtectedRoute>
                    <TaxReport />
                  </ProtectedRoute>
                } />
                <Route path="/auth-callback" element={<AuthCallback />} />
                <Route 
                  path="/client-dashboard" 
                  element={
                    <ProtectedRoute requiredUserType="client">
                      <ClientDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/specialist-dashboard" 
                  element={
                    <ProtectedRoute requiredUserType="specialist">
                      <SpecialistDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route path="/booking-success" element={<PaymentSuccess />} />
                <Route path="/blog" element={<BlogList />} />
                <Route path="/blog/:slug" element={<BlogArticle />} />
                <Route path="/guarantee" element={<GuaranteePage />} />
                <Route path="/update-phone" element={<UpdatePhonePage />} />
                <Route path="/stripe-help" element={<StripeHelpPage />} />
                <Route path="/stripe-guide" element={<StripeGuidePage />} />
                <Route path="/game/bubbles" element={<CleanBubblesGame />} />
                <Route path="/games" element={<GamesHub />} />
                <Route path="/pay/:token" element={<GuestPaymentPage />} />
                <Route path="/quiz" element={<MatchQuiz />} />
                <Route path="/game/remont" element={<RenovationGame />} />
                <Route path="/discover" element={<MatchQuiz />} />
                <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  </Suspense>
                  <Toaster />
                  <Suspense fallback={null}>
                    <HelpWidget />
                    <CalcWidgetWrapper />
                    <ReviewPromptModal onOpenReview={(booking) => {
                    // Navigate based on user's role in the booking
                    console.log('🔍 App.js onOpenReview called:', {
                      booking_id: booking.id,
                      is_user_provider: booking.is_user_provider,
                      navigating_to: booking.is_user_provider ? 'booking-requests' : 'my-bookings'
                    });
                    if (booking.is_user_provider) {
                      // User is the provider - go to booking requests
                      window.location.href = `/booking-requests?review=${booking.id}`;
                    } else {
                      // User is the client - go to my bookings
                      window.location.href = `/my-bookings?review=${booking.id}`;
                    }
                  }} />
                  </Suspense>
                </div>
              </BrowserRouter>
            </WalletProvider>
          </BookingProvider>
          </InboxProvider>
          </FooterProvider>
        </LocationModalWrapper>
        </AuthProvider>
      </APIProvider>
    </CurrencyProvider>
  );
}

export default App;